import { Dispatch, FC, SetStateAction, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { FeedbackBanner as FeedbackBannerButton } from './feedback-banner';
import { FeedbackFabButton } from './feedback-fab-button';
import { FeedbackPanel } from './feedback-panel';
import { trackClickFeedback, trackFeedbackSubmission, trackRating, trackResetFeedback } from './feedback.newrelic';
import * as GATracking from './feedback.ga';
import { FeedbackMode as Mode, SecondaryBtn, SupportHint, useFeedback } from './use-feedback';

export const CLOSING_TIMEOUT = 5000;
export const TRANSITION_DURATION = 200;

export const Feedback: FC<
  React.PropsWithChildren<{
    setShowFeedbackPanel: Dispatch<SetStateAction<boolean>>;
    question?: string;
    feedbackLabels?: [string, string];
    feedbackTextRating?: string;
    active: boolean;
    showFeedbackBanner: boolean;
    showFeedbackFab: boolean;
    feedbackMode: Mode;
    trackingContexts: [string, string];
    reverse?: boolean;
    secondaryButton?: SecondaryBtn;
    supportHint?: SupportHint;
    additionalFeedbackUrl?: string;
    eventName?: string;
  }>
> = ({
  children,
  setShowFeedbackPanel,
  showFeedbackBanner,
  question,
  feedbackLabels,
  active,
  feedbackTextRating,
  feedbackMode,
  trackingContexts,
  showFeedbackFab,
  reverse = false,
  secondaryButton,
  supportHint,
  additionalFeedbackUrl,
  eventName,
}) => {
  const { isFeedbackPanelOpen } = useFeedback();
  const [ratingValue, setRatingValue] = useState<number>();
  const [panelContext, bannerContext] = trackingContexts;
  const { t } = useTranslation('shared');
  const feedbackBtnLabel = t('userFeedback_badge_label');
  const feedbackBannerButtonRef = useRef<HTMLButtonElement>(null);

  if (!question && active) {
    throw new Error('Please set a question before you use the feedback feature');
  }

  const resetState = () => {
    setShowFeedbackPanel(false);
    setRatingValue(undefined);
    trackResetFeedback(panelContext);
  };

  const sendRating = (ratingValue: number) => {
    setRatingValue(ratingValue);
    trackRating({
      feedbackMode,
      panelContext,
      question: question ?? '',
      ratingValue,
    });
  };

  const onSubmit = ({ textRating = '' }) => {
    trackFeedbackSubmission({ content: textRating, panelContext, question: question ?? '', rating: ratingValue as number });
    eventName &&
      GATracking.trackFeedbackSubmission({
        eventName,
        context: panelContext,
        question: question ?? '',
        feedbackText: textRating,
        [feedbackMode === Mode.Scale ? 'starRating' : 'thumbsRating']: ratingValue ?? null,
      });
    setTimeout(() => {
      resetState();
    }, CLOSING_TIMEOUT);
  };

  const onClickFeedbackButton = () => {
    setShowFeedbackPanel(true);
    trackClickFeedback(bannerContext);
  };

  return (
    <>
      {children}
      {active && (
        <div aria-live="polite">
          <FeedbackBannerButton
            label={feedbackBtnLabel}
            visible={!isFeedbackPanelOpen && showFeedbackBanner}
            onClick={onClickFeedbackButton}
            aria-haspopup="dialog"
            reverse={reverse}
            ref={feedbackBannerButtonRef}
          />
          <FeedbackFabButton
            label={feedbackBtnLabel}
            visible={!isFeedbackPanelOpen && showFeedbackFab}
            aria-haspopup="dialog"
            ref={feedbackBannerButtonRef}
          />
          <FeedbackPanel
            visible={isFeedbackPanelOpen}
            ratingValue={ratingValue}
            onClickClose={resetState}
            onClickRating={sendRating}
            onClickSubmit={onSubmit}
            question={question ?? ''}
            feedbackLabels={feedbackLabels}
            feedbackTextRating={feedbackTextRating}
            feedbackMode={feedbackMode}
            secondaryButton={secondaryButton}
            supportHint={supportHint}
            additionalFeedbackUrl={additionalFeedbackUrl}
            onReset={() => feedbackBannerButtonRef.current?.focus()}
          />
        </div>
      )}
    </>
  );
};
